import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import PropTypes from 'prop-types';
import CircularProgress from '@mui/material/CircularProgress';

import { getTickerData, getXMLData } from '../../api/ticker';
import { useGlobalData } from '../../context/globalData';
import { useNotification } from '../../context/notification';
import { languageColor } from '../../config';
import { useAuth } from '../../context/auth';

import FatalError from '../../components/FatalError';
import TabsComponent from './TabsComponent';
import Carousels from './Carousels';
import convertTinyInt from '../../utils';

function Ticker(props) {
  const { setNotifications } = props;
  const { addData } = useGlobalData();
  const { user } = useAuth();
  const { addNotification } = useNotification();
  const [languagesTab, setLanguagesTab] = useState([]);
  const [stopQuery, setStopQuery] = useState(false);
  const [settings, setSettings] = useState(null);

  useQuery('xmlData', getXMLData, {
    onError: (err) => {
      setStopQuery(true);
      addNotification({ message: `Erreur lors de l'écriture du xml: ${err.message}`, severity: 'error' });
    },
  });

  useEffect(() => {
    fetch('../../appsettings.json')
      .then((response) => response.json())
      .then((data) => setSettings(data))
      .catch((error) => console.error('Error loading config:', error));
  }, []);

  const sortLang = (languages) => {
    if (languages.length === 4) {
      const result = [null, null, null, null];
      for (let i = 0; i < languages.length; i++) {
        if (languages[i].label === 'ar') {
          result[0] = languages[i];
          result[0].orderNum = 1
        }
        if (languages[i].label === 'en') {
          result[1] = languages[i];
          result[1].orderNum = 2
        }
        if (languages[i].label === 'fr') {
          result[2] = languages[i];
          result[2].orderNum = 3
        }
        if (languages[i].label === 'es') {
          result[3] = languages[i];
          result[3].orderNum = 4
        }
      }

      return result
    }

    return languages;
  }

  const { isFetching, isLoading, error } = useQuery('tickerData', getTickerData, {
    onSuccess: (data) => {
      addData(data);

      const languages = sortLang(data.langues
        ?.filter((langue) => convertTinyInt(user.rights[langue.label])));

      const carouselsTab = (id) =>
        data.carousels
          .filter((carousel) => {
            if ((carousel.type === 3 || carousel.type === 4) && !user.rights.promo) {
              return false;
            }
            if ((carousel.label === "EVENT") && !user.rights.event){
              return false;
            }
            if (user.rights.promo && !user.rights.admin) {
              return [3, 4].includes(carousel.type) && carousel.languageID === id;
            }
            return carousel.languageID === id;
          })
          .map((carousel) => ({
            title: carousel.label,
            id: carousel.carouselID,
            status: `carousel_${carousel.type}`,
            content: (
              <Carousels
                setNotifications={setNotifications}
                carousel={carousel}
                messages={data.messages
                  .filter((msg) => msg.carouselID === carousel.carouselID)}
                tickerData={data}
                languages={languages}
                settings={settings}
              />
            ),
          }));

      setLanguagesTab(
        languages
          ?.map((langue) => ({
            title: langue.label,
            id: langue.languageID,
            systemRuning: langue.active,
            color: languageColor.filter((item) => item.langueID === langue.languageID)[0].color,
            content: (
              <TabsComponent
                className="tabsCarousel"
                lang={{ label: langue.label }}
                tabConfig={carouselsTab(langue.languageID)}
                settings={settings}
              />
            ),
          })),
      );
    },
    onError: (err) => {
      setStopQuery(true);
      addNotification({ message: `Erreur lors de la récupération des données: ${err.message}`, severity: 'error' });
    },
    refetchInterval: 2 * 1000,
    enabled: !stopQuery,
  });

  if (error) {
    return (
      <div style={{ height: '100vh' }}>
        <FatalError error="Session expirée" />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <CircularProgress size={20} />
      </div>
    );
  }

  if (languagesTab?.length < 1 && !isFetching) {
    return <FatalError error="Aucune donnée récupérée" />;
  }

  return <TabsComponent type="lang" tabConfig={languagesTab} className="tabsLangue" />;
}

Ticker.propTypes = {
  setNotifications: PropTypes.func,
};
Ticker.defaultProps = {
  setNotifications: () => { },
};

export default Ticker;
